import { useQuery } from '@tanstack/react-query';
import { useNavigate, useParams } from '@tanstack/react-router';
import type { ProjectId } from '@voll-intelligence/domain';
import { queryKeys } from '@web/core/query';
import { projectsRepository } from '@web/data/repositories';
import { t } from '@web/i18n';
import { AppIcons, Button, Card, isAppIconName, Scaffold, useOrgContext } from '@web/ui/core';
import { fireAndForget } from '@web/ui/core/utils';

export function ProjectSquadsPage(): React.JSX.Element | null {
  const { projectId: projectIdParam } = useParams({ strict: false });
  const projectId = projectIdParam as ProjectId;
  const navigate = useNavigate();
  const { orgId, orgSlug } = useOrgContext();

  const { data: project, isLoading } = useQuery({
    queryKey: queryKeys.projects.detail(orgId, projectId),
    queryFn: () => projectsRepository.getProject(orgId, projectId),
  });

  if (isLoading || !project) return null;

  const squads = project.squads;
  const projectCode = project.code;
  const projectIcon = isAppIconName(project.icon) ? project.icon : 'users-four';
  const canEdit = project.permissions.includes('kanban:projects:update');

  const goCreate = (): void => {
    fireAndForget(
      navigate({
        to: '/orgs/$orgSlug/squad/new',
        params: { orgSlug },
        search: { projectId },
      }),
    );
  };

  const goEdit = (squadId: string): void => {
    fireAndForget(
      navigate({
        to: '/orgs/$orgSlug/squad/$squadId/edit',
        params: { orgSlug, squadId },
      }),
    );
  };

  return (
    <Scaffold
      body={
        <>
          <div className="mb-3.5">
            <h4 className="m-0 text-base font-medium text-foreground">
              {t('project.editor.squads.title')}
            </h4>
            <p className="m-0 text-meta text-muted-foreground">
              {t('project.editor.squads.hint')}{' '}
              <code className="rounded-sm border border-border bg-muted px-1.5 font-mono text-micro text-foreground-default">
                {projectCode}.NOME_DO_SQUAD
              </code>
            </p>
          </div>

          {squads.length === 0 ? (
            <Card>
              <div className="py-7 text-center">
                <div className="mb-2 inline-flex h-10 w-10 items-center justify-center rounded-md bg-muted text-muted-foreground">
                  <AppIcons name="users-four" size={20} />
                </div>
                <div className="text-card-meta font-medium text-foreground">
                  {t('project.editor.squads.empty.title')}
                </div>
                <div className="mt-0.5 text-meta text-muted-foreground">
                  {t('project.editor.squads.empty.hint')}
                </div>
              </div>
            </Card>
          ) : (
            <ul className="mt-3 flex list-none flex-col gap-1.5 p-0">
              {squads.map((squad) => (
                <li key={squad.id}>
                  <Card
                    onTap={
                      canEdit
                        ? () => {
                            goEdit(squad.id);
                          }
                        : undefined
                    }
                    aria-label={`${t('project.editor.squads.actions.edit')} ${squad.name}`}
                  >
                    <div className="grid grid-cols-tile items-center gap-3 p-2.5">
                      <span
                        aria-hidden
                        className="inline-flex h-9.5 w-9.5 items-center justify-center rounded-md border border-border bg-muted text-muted-foreground"
                      >
                        <AppIcons name={projectIcon} size={18} />
                      </span>
                      <span className="flex min-w-0 flex-col gap-0.5">
                        <span className="truncate text-card-meta font-medium text-foreground">
                          {squad.name}
                        </span>
                        <span className="inline-flex items-center gap-pill-track truncate font-mono text-card-id text-muted-foreground">
                          <AppIcons name="hash" size={10} />
                          {`${projectCode}.${squad.code}`}
                        </span>
                      </span>
                      <AppIcons name="caret-right" size={14} />
                    </div>
                  </Card>
                </li>
              ))}
            </ul>
          )}

          {canEdit ? (
            <div className="mt-3.5 flex justify-end">
              <Button variant="outline" onClick={goCreate}>
                <AppIcons name="plus" size={13} />
                <span>{t('project.editor.squads.actions.create')}</span>
              </Button>
            </div>
          ) : null}
        </>
      }
    />
  );
}
